import type { ReactNode } from 'react'

import { cn, insetFocusRing } from './cn'

/** The region's accessible name: its own text, or the id of the element that names it. */
type RegionName = { readonly label: string; readonly labelledBy?: never } | { readonly labelledBy: string; readonly label?: never }

export type ScrollRegionProps = RegionName & {
  readonly className?: string
  readonly children: ReactNode
}

/**
 * A box that scrolls sideways, for a table that must stay a grid on a narrow screen, such as one
 * with a column per session. It takes focus and is named, so the keyboard can scroll it (WCAG 2.1.1).
 * A list of records uses the shared results table instead, which stacks its rows. It is `relative`
 * so absolutely placed content, such as a check box's sr-only input, scrolls inside it rather than
 * widening the page.
 */
export function ScrollRegion(props: ScrollRegionProps) {
  return (
    <section
      aria-label={props.label}
      aria-labelledby={props.labelledBy}
      // oxlint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- a scroll region must take focus
      tabIndex={0}
      className={cn('relative overflow-x-auto', insetFocusRing, props.className)}
    >
      {props.children}
    </section>
  )
}
